import { LocationStrategy } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { CallForTender } from 'src/app/core/_models/callForTender';
import { Indicator } from 'src/app/core/_models/indicator';
import { Project } from 'src/app/core/_models/project';
import { Scenario } from 'src/app/core/_models/scenario';
import { Solution } from 'src/app/core/_models/solution';
import { Version } from 'src/app/core/_models/version';
import { AuthService } from 'src/app/core/_services/auth.service';
import { CallForTenderService } from 'src/app/core/_services/call-for-tender.service';
import { ProjectService } from 'src/app/core/_services/project.service';
import { SolutionService } from 'src/app/core/_services/solution.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { SnackbarTypes } from '../../core/_enums/snackbar.types';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../../core/_services/snackbar.service';
import { BreadcrumbItem } from '../shared/breadcrumbs/breadcrumbs.component';
import { ConfirmCancelDialogComponent } from '../shared/confirm-cancel-dialog/confirm-cancel-dialog.component';
import { DialogImportProjectComponent } from '../shared/dialog-import-project/dialog-import-project.component';
import { DialogSelectSolutionComponent } from '../shared/dialog-select-solution/dialog-select-solution.component';
import { RowReorderEvent, TableColumn, TableRow } from '../shared/table/table.component';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';

@Component({
  selector: 'app-call-for-tender',
  templateUrl: './call-for-tender.component.html',
  styleUrls: ['./call-for-tender.component.scss']
})
export class CallForTenderComponent implements OnInit, OnDestroy {
  SiteMapUrl = SiteMapUrl;
  callForTenderId: string | null = null;
  currentCallForTender: CallForTender | null = null;
  private destroy$ = new Subject<void>();

  allRows: TableRow[] = [];
  displayedRows: TableRow[] = [];
  rowsToShow = 50;
  dbVersions: Version[] = [];
  selectedVersionId: string = '';
  scenario: Scenario | null = null;
  indicators: Indicator[] = [];
  hasModifications: boolean = false;
  syntheseRequested: boolean = false;
  allProjectsGrouped: Project[] = [];
  scenarioRefreshKey: number = 0;

  tableColumns: TableColumn[] = [];
  detailTitle = '';

  constructor(
    private callForTenderService: CallForTenderService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private spinnerService: SpinnerOverlayService,
    private versionService: VersionService,
    private projectService: ProjectService,
    private authService: AuthService,
    private matDialog: MatDialog,
    private solutionService: SolutionService,
    private locationStrategy: LocationStrategy,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private labelI18n: LabelI18nService
  ) {}

  ngOnInit(): void {
    this.initTableColumns();
    this.setupRouteWatcher();
    this.loadVersions();
    this.loadIndicators();
    this.loadAllProjectsGrouped();
  }

  private initTableColumns(): void {
    this.tableColumns = [
      { key: 'index', label: '#', align: 'left', cssClass: 'index-column', sortable: false },
      { key: 'organisationName', label: 'cft_col_organisation', align: 'left', cssClass: 'flex-column', sortable: false },
      { key: 'projectName', label: 'cft_col_project_name', align: 'left', cssClass: 'flex-column', customTemplate: true, sortable: false },
      { key: 'solutionName', label: 'cft_col_solution_name', align: 'left', cssClass: 'flex-column', customTemplate: true, sortable: false },
      { key: 'co2', label: 'cft_col_co2', align: 'left', cssClass: 'fit-content', sortable: false }
    ];
  }

  private loadVersions(): void {
    this.versionService.getAll().subscribe({
      next: (versions) => {
        this.dbVersions = versions;
      }
    });
  }

  private loadAllProjectsGrouped(): void {
    this.solutionService.getAllGroupedByProject(this.selectedVersionId).subscribe({
      next: (projects) => {
        this.allProjectsGrouped = projects;
      }
    });
  }

  onVersionChange(value: string): void {
    if (!this.callForTenderId) return;
    this.selectedVersionId = value;
    this.callForTenderService.updateVersion(this.callForTenderId, value).subscribe({
      next: () => {
        this.snackbarService.open(SnackbarTypes.update);
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    });
  }

  ngOnDestroy(): void {
    this.spinnerService.hide();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupRouteWatcher(): void {
    this.activeRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.loadCallForTender(params['id']);
    });
  }

  private loadCallForTender(id: string, onComplete?: () => void): void {
    if (id !== this.callForTenderId) {
      this.syntheseRequested = false;
      this.hasModifications = false;
    }
    if (!id) {
      this.callForTenderId = null;
      this.currentCallForTender = null;
      return;
    }

    this.callForTenderId = id;
    this.spinnerService.show();
    this.callForTenderService.get(id).subscribe({
      next: (callForTender: CallForTender | null) => {
        this.currentCallForTender = callForTender;
        if (callForTender) {
          this.detailTitle = this.translate.instant('cft_detail_title', { code: callForTender.code, name: callForTender.displayName });
        }
        if (callForTender?.versionId) {
          this.selectedVersionId = callForTender.versionId;
        }
        this.updateRows();
        this.buildScenario();
        this.refreshSynthese();
        this.spinnerService.hide();
        onComplete?.();
      },
      error: () => {
        this.currentCallForTender = null;
        this.spinnerService.hide();
      }
    });
  }

  private updateRows(): void {
    if (!this.currentCallForTender?.rows) {
      this.allRows = [];
      this.displayedRows = [];
      return;
    }
    this.allRows = this.currentCallForTender.rows
      .filter((row) => row.solutions && row.solutions.length > 0)
      .sort((a, b) => a.rank - b.rank)
      .map((row) => {
        const selectedSolution = row.solutions.find((s) => s.id === row.selectedSolutionId) || row.solutions[0];
        const project = this.allProjectsGrouped.find((p) => p.id === row.projectId);
        const organisationName = row.organisationName || project?.owner || project?.scopeName || '';

        return {
          index: row.rank,
          organisationName,
          projectName: row.displayName || '',
          solutionName: selectedSolution.displayName || '',
          co2: row.tCo2Value || 0,
          resourceId: selectedSolution.id,
          projectId: row.projectId,
          selectedSolutionId: row.selectedSolutionId || selectedSolution.id,
          rowId: row.id
        };
      });

    this.displayedRows = this.allRows.slice(0, this.rowsToShow);
  }

  getSolutionsForProject(projectId: string): Solution[] {
    const project = this.allProjectsGrouped.find((p) => p.id === projectId);
    return project?.solutions || [];
  }

  getSolutionsForRow(row: TableRow): Solution[] {
    return this.getSolutionsForProject(String(row.projectId));
  }

  getRowDragPreviewLabel(row: TableRow | null | undefined): string {
    if (!row) return '';
    const label = row.solutionName ?? row.projectName ?? row.organisationName ?? '';

    return String(label);
  }

  getProjectId(row: TableRow): string {
    return String(row.projectId);
  }

  onSolutionChange(rowId: string, solutionId: string): void {
    if (!this.callForTenderId) return;

    this.callForTenderService.patchSelectedSolutionId(this.callForTenderId, rowId, solutionId).subscribe({
      next: () => {
        this.snackbarService.open(SnackbarTypes.update);
        if (this.callForTenderId) {
          this.loadCallForTender(this.callForTenderId);
        }
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    });
  }

  onCreateProject(): void {
    const perimeterId = this.authService.roleSubject.value.perimeterId;
    if (!perimeterId || !this.callForTenderId) return;

    const urlTree = this.router.createUrlTree([`${SiteMapUrl.MenuProject}/new`], {
      queryParams: { fromCft: this.callForTenderId }
    });
    const url = this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(urlTree));
    window.open(url, '_blank');
  }

  onImportProject(): void {
    const perimeterId = this.authService.roleSubject.value.perimeterId;
    if (!perimeterId) return;

    const dialogRef = this.matDialog.open(DialogImportProjectComponent, {
      data: { perimeterId, navigateAfterImport: false }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.success || !this.callForTenderId || !result.projectId) return;
      this.linkImportedProjectToCft(result.projectId);
    });
  }

  private linkImportedProjectToCft(importedProjectId: string): void {
    const callForTenderId = this.callForTenderId;
    if (!callForTenderId) return;

    this.spinnerService.show();
    this.solutionService.getAllGroupedByProject(this.selectedVersionId).subscribe({
      next: (projects) => {
        this.allProjectsGrouped = projects;
        const project = projects.find((p) => p.id === importedProjectId);
        const firstSolution = project?.solutions?.[0];

        if (!project?.id || !firstSolution?.id) {
          this.spinnerService.hide();
          this.snackbarService.open(this.translate.instant('cft_import_no_solution_error'));
          return;
        }

        this.callForTenderService
          .linkProject(callForTenderId, {
            cftId: callForTenderId,
            projectId: project.id,
            selectedSolutionId: firstSolution.id,
            rank: 0
          })
          .subscribe({
            next: () => {
              this.spinnerService.hide();
              this.loadCallForTender(callForTenderId);
              this.snackbarService.open(SnackbarTypes.add);
            },
            error: () => {
              this.spinnerService.hide();
              this.snackbarService.open(SnackbarTypes.error);
            }
          });
      },
      error: () => {
        this.spinnerService.hide();
        this.snackbarService.open(SnackbarTypes.error);
      }
    });
  }

  onSelectProject(): void {
    const perimeterId = this.authService.roleSubject.value.perimeterId;
    const dialogRef = this.matDialog.open(DialogSelectSolutionComponent, {
      width: '1100px',
      maxWidth: '95vw',
      data: { perimeterId, selectedSolution: null, versionId: this.selectedVersionId }
    });

    dialogRef.afterClosed().subscribe((result) => {
      const callForTenderId = this.callForTenderId;
      if (result && result.id && callForTenderId) {
        const project = this.allProjectsGrouped.find((p) => p.solutions?.some((s) => s.id === result.id));
        if (project && project.id && project.displayName) {
          // TODO: KFC
          // On devrait etre capable de récup toutes ces infos seulement a partir de l'id de la solution pour ne pas avoir a faire ce traitement
          this.callForTenderService.linkProject(callForTenderId, { cftId: callForTenderId, projectId: project.id, selectedSolutionId: result.id, rank: 0 }).subscribe({
            next: () => {
              if (callForTenderId) {
                this.loadAllProjectsGrouped();
                this.loadCallForTender(callForTenderId);
              }
              this.snackbarService.open(SnackbarTypes.update);
            },
            error: () => {
              this.snackbarService.open(SnackbarTypes.error);
            }
          });
        }
      }
    });
  }

  onRowDelete(row: TableRow): void {
    const rowId = row.rowId as string;
    const callForTenderId = this.callForTenderId;
    if (!rowId || !callForTenderId) return;

    const dialogRef = this.matDialog.open(ConfirmCancelDialogComponent, {
      data: {
        message: this.translate.instant('cft_confirm_delete_project', { name: row.projectName || '' }),
        confirmText: this.translate.instant('common_btn_delete'),
        cancelText: this.translate.instant('common_btn_cancel'),
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.spinnerService.show();
      this.callForTenderService.deleteRow(callForTenderId, rowId).subscribe({
        next: () => {
          this.spinnerService.hide();
          this.loadCallForTender(callForTenderId);
          this.snackbarService.open(SnackbarTypes.update);
        },
        error: () => {
          this.spinnerService.hide();
          this.snackbarService.open(SnackbarTypes.error);
        }
      });
    });
  }

  onRowsReordered(event: RowReorderEvent): void {
    if (!this.callForTenderId || !this.currentCallForTender || !event.rows?.length) return;

    const reorderedRows = event.rows.map((row, index) => ({
      ...row,
      index: index + 1
    }));

    this.allRows = reorderedRows;
    this.displayedRows = this.allRows.slice(0, this.rowsToShow);

    const rankUpdates = this.allRows
      .map((row, index) => ({
        rowId: row.rowId as string | undefined,
        rank: index + 1
      }))
      .filter((item) => item.rowId)
      .map((item) => ({ rowId: item.rowId as string, rank: item.rank }));

    if (!rankUpdates.length) return;

    const rowsWithSolutions = this.currentCallForTender.rows.filter((row) => row.solutions?.length > 0);
    const solutionRowMap = new Map(rowsWithSolutions.map((row) => [row.id, row]));

    const reorderedCallForTenderRows = rankUpdates
      .map(({ rowId, rank }) => {
        const row = solutionRowMap.get(rowId);
        if (!row) return undefined;
        row.rank = rank;
        return row;
      })
      .filter((row): row is (typeof rowsWithSolutions)[number] => Boolean(row));

    const otherRows = this.currentCallForTender.rows.filter((row) => !solutionRowMap.has(row.id));
    this.currentCallForTender.rows = [...reorderedCallForTenderRows, ...otherRows];

    this.buildScenario();
    this.hasModifications = true;

    this.callForTenderService.updateRowRanks(this.callForTenderId, rankUpdates).subscribe({
      next: () => {
        this.snackbarService.open(SnackbarTypes.update);
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    });
  }

  showMore(): void {
    this.rowsToShow += 50;
    this.displayedRows = this.allRows.slice(0, this.rowsToShow);
  }

  get hasMoreRows(): boolean {
    return this.displayedRows.length < this.allRows.length;
  }

  navigateToCallForTenders(): void {
    this.router.navigate([SiteMapUrl.MenuCallForTender]);
  }

  get breadcrumbItems(): BreadcrumbItem[] {
    if (!this.callForTenderId || !this.currentCallForTender) {
      return [];
    }

    return [
      {
        label: this.translate.instant('cft_breadcrumb_all'),
        action: () => this.navigateToCallForTenders()
      },
      {
        label: this.currentCallForTender.displayName || ''
      }
    ];
  }

  private loadIndicators(): void {
    this.projectService.getIndicators().subscribe({
      next: (indicators: Indicator[]) => {
        this.indicators = indicators || [];
        this.buildScenario();
      }
    });
  }

  // A supprimer, on doit pouvoir récup la synthèse à partir d'un cftId sans scenario
  private buildScenario(): void {
    if (!this.currentCallForTender || !this.indicators.length) {
      this.scenario = null;
      return;
    }

    const allSolutions = this.currentCallForTender.rows
      .flatMap((row) => row.solutions || [])
      .map((solution, index) => ({
        ...solution,
        rank: index + 1
      }));

    this.scenario = {
      scenarioId: this.callForTenderId || undefined,
      code: `CALL_FOR_TENDER_${this.callForTenderId}`,
      displayName: this.currentCallForTender.displayName,
      projectId: undefined,
      solutions: allSolutions,
      isHybrid: false,
      indicatorSelected: this.indicators.map((indicator) => ({
        ...indicator,
        isChecked: this.currentCallForTender?.selectedIndicatorsIds?.some((i) => i === indicator.indicatorId) || false
      })),
      versionId: this.currentCallForTender.versionId
    };
  }

  showSynthese(): void {
    this.syntheseRequested = true;
    this.refreshSynthese();
  }

  onSyntheseLoadError(): void {
    this.syntheseRequested = false;
  }

  refreshSynthese(): void {
    this.hasModifications = false;
    this.scenarioRefreshKey++;
  }

  getIndicatorLabel(indicator: Indicator | null | undefined): string {
    if (!indicator) return '';
    const key = `cft_indicator_${indicator.code ?? ''}`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) return translated;
    return indicator.displayName ?? '';
  }

  onIndicatorChange(indicator: Indicator, event: any): void {
    if (this.scenario) {
      const scenarioIndicator = this.scenario.indicatorSelected.find((ind) => ind.indicatorId === indicator.indicatorId);
      if (scenarioIndicator) {
        scenarioIndicator.isChecked = event.checked;
        this.hasModifications = true;
        if (this.callForTenderId) {
          this.callForTenderService.updateIndicators(this.callForTenderId, this.scenario.indicatorSelected).subscribe({
            next: () => {
              this.snackbarService.open(SnackbarTypes.update);
            },
            error: () => {
              this.snackbarService.open(SnackbarTypes.error);
            }
          });
        }
      }
    }
  }

  get hasSolutions(): boolean {
    return this.currentCallForTender?.rows?.some((row) => row.solutions && row.solutions.length > 0) || false;
  }

  onSave(): void {
    if (!this.callForTenderId) return;
    this.loadAllProjectsGrouped();
    this.loadCallForTender(this.callForTenderId, () => {
      this.snackbarService.open(SnackbarTypes.update);
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(this.labelI18n.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
}
